import './index.css'
import groupBy from 'lodash/groupBy'
import uniq from 'lodash/uniq'

import React from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { NativeCategory, NativeTag, Track } from 'types/track'
import { removeNativeTagFromTracks } from '@disco/state-management/actions/PlaylistTrackActions'
import {
  clearSearch,
  getSearchResults,
  updateSearchParams,
} from '@disco/state-management/actions/SearchActions'
import mixpanel from '@disco/api/mixpanel'
import { COLOR_INDICATORS } from '@disco/constants/FeatureTypes'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import NativeTagsMenu from 'components/common/native-tags-menu'
import Tag from 'components/ui/tag'
type Props = {
  track: Track
  readOnly?: boolean
}

/**
 * Native tags pane component that displays track tags grouped by category
 * Shows tags with color indicators, handles tag clicks for search, and tag removal
 * Integrates with Mixpanel for analytics and supports read-only modes
 * @param track - Track object containing native tags to display
 * @param readOnly - Whether the pane should be in read-only mode
 */
const NativeTagsPane = (props: Props): null | React.ReactElement<'div'> => {
  const dispatch = useDispatch()
  const isPhone = useSelector(isDeviceTypePhone)
  const tagsByCat = groupBy(props.track.native_tags, 'category_id')
  // const [wasEmpty, setWasEmpty] = useState(tagsByCat.length === 0)
  const colorIndicatorsEnabled = useFeatureEnabled(COLOR_INDICATORS)
  const isNativeTagsReadonly: boolean =
    useSelector(
      state => state.user.business?.private_metadata.is_native_tags_readonly,
    ) || props.readOnly
  const nativeCategories: {
    [key in NativeCategory['id']]?: NativeCategory
  } = useSelector(state => state.entities.nativeCategories)
  const search = useSelector(state => state.search)

  // if (!wasEmpty && props.track.native_tags.length === 0) {
  //   setWasEmpty(true)
  // }
  const handleTagClick = (tag: NativeTag) => () => {
    const params = {
      nativeTags: uniq(search.nativeTags.concat(tag.id)),
    }
    mixpanel.sendPureEvent('Clicking on track tag (faceted & track pane)', {
      Tags: [tag.name],
      Type: 'Track pane',
    })
    dispatch(clearSearch(false))
    dispatch(updateSearchParams(params))
    dispatch(getSearchResults())
  }

  const handleTagRemove = (tag: NativeTag) => () => {
    dispatch(removeNativeTagFromTracks([props.track.id], tag))
  }

  if (isPhone && props.track.native_tags?.length === 0) return null
  return (
    <div className="NativeTagsPane" data-testid="native-tags-pane">
      {Object.keys(tagsByCat)
        .map(Number)
        .map((id: NativeCategory['id']) => (
          <div key={id} className="NativeTagsPane_Item">
            <div className="NativeTagsPane_Category" data-testid="native-tags-category">
              {nativeCategories[id]?.name}
            </div>
            {tagsByCat[id].map((tag: NativeTag) => (
              <span className="NativeTagsPane_Tag" key={tag.id}>
                <Tag
                  tag={tag.name}
                  tagColor={colorIndicatorsEnabled ? tag.color : undefined}
                  onTagClick={isPhone ? undefined : handleTagClick(tag)}
                  onRemoveClick={
                    isNativeTagsReadonly || isPhone || props.track?.read_only
                      ? undefined
                      : handleTagRemove(tag)
                  }
                />
              </span>
            ))}
          </div>
        ))}
      {props.track.native_tags?.length === 0 && !props.readOnly && (
        <NativeTagsMenu
          trackIds={[props.track.id]}
          assignedTags={props.track.native_tags || []}
          showLabel
        />
      )}
    </div>
  )
}

export default NativeTagsPane
